// src/components/auth/ProtectedRoute.tsx
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasRequiredRole } from '../../utils/roleUtils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  allowGuest?: boolean;
  requiresVendor?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = [],
  allowGuest = false,
  requiresVendor = false
}) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect authenticated users away from login pages
  if (user) {
    if (location.pathname === '/login') {
      return <Navigate to="/" replace />;
    }
    if (location.pathname === '/staff/login') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (allowGuest) {
    if (user && allowedRoles.length > 0 && !hasRequiredRole(user.roles, allowedRoles)) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  if (!user) {
    const isStaffRoute = allowedRoles.some(role =>
      ['admin', 'manager', 'staff', 'driver', 'warehouse'].includes(role)
    );

    const loginPath = isStaffRoute ? '/staff/login' : '/login';
    return <Navigate to={`${loginPath}?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Check vendor requirement
  if (requiresVendor) {
    // Check if user has vendor role/group
    const isVendor = user?.roles?.includes('vendor') || user?.is_vendor || false;

    // If not vendor, redirect to apply page
    if (!isVendor) {
      return <Navigate to="/vendor/apply" replace />;
    }

    // Check if vendor profile is active
    if (user?.vendor_profile && !user.vendor_profile.is_active) {
      // If vendor account is deactivated, redirect to home or contact page
      return <Navigate to="/" replace />;
    }
  }

  if (allowedRoles.length > 0 && !hasRequiredRole(user.roles, allowedRoles)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;